import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { tasksApi, type HumanTask } from "../../api/tasks";
import { useToast } from "../../components/toast/context";
import { closeLabel } from "../workThrough";

/**
 * Close an agent-proposed human task from the queue, a work-through run or a
 * campaign bundle: Done / Sent ✓, or Decline with a reason. Both close
 * reversibly, so each toast carries Undo (`POST /tasks/{id}/reopen`). Every
 * write repaints the task family and the campaign console.
 */
export function useTaskActions() {
  const qc = useQueryClient();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["tasks"] }),
      qc.invalidateQueries({ queryKey: ["console"] }),
    ]);

  async function reopen(task: HumanTask, onUndo?: () => void) {
    try {
      await tasksApi.reopen(task.task_id);
      onUndo?.();
      await refresh();
      toast.ok("Back in your queue");
    } catch (err) {
      toast.error(err);
    }
  }

  async function run(
    task: HumanTask,
    write: () => Promise<unknown>,
    message: string,
    onUndo?: () => void,
  ): Promise<boolean> {
    setBusyId(task.task_id);
    try {
      await write();
      await refresh();
      toast.ok(message, { action: { label: "Undo", onClick: () => void reopen(task, onUndo) } });
      return true;
    } catch (err) {
      toast.error(err);
      return false;
    } finally {
      setBusyId(null);
    }
  }

  return {
    busyId,
    /** Done / Sent ✓ – optional feedback for the proposing seat. */
    complete: (task: HumanTask, opts: { feedback?: string; onUndo?: () => void } = {}) =>
      run(
        task,
        () => tasksApi.complete(task.task_id, opts.feedback ? { feedback: opts.feedback } : {}),
        `${closeLabel(task) === "Sent ✓" ? "Sent" : "Done"} – ${task.contact_name || task.title || "task"}`,
        opts.onUndo,
      ),
    /** Decline – the reason goes back to the proposing seat. */
    decline: (task: HumanTask, reason: string, opts: { onUndo?: () => void } = {}) =>
      run(
        task,
        () => tasksApi.dismiss(task.task_id, { reason }),
        `Declined – ${task.contact_name || task.title || "task"}`,
        opts.onUndo,
      ),
  };
}
